import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  useBoolean,
  useOutsideClick,
  FormControl,
  FormLabel,
  useMediaQuery,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import BranchIcon from 'components/Icons/BranchIcon';
import FigmaCheckCircleIcon from 'components/Icons/FigmaCheckCircleIcon';
import FigmaCodeCompareIcon from 'components/Icons/FigmaCodeCompareIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import PlanPermission from 'container/PlanPermission';
import { fetchAllScenarios, getAllScenarios } from 'redux/ScenarioSlice';
import { Scenario } from 'models/response/scenario.interface';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  getSelectedDashboardScenario,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  getCompareDashboardScenario,
  getDashboard,
  fetchAllDataValues,
  getSelectedPage,
  getTimeFilter,
  getIsPageBarOpen,
} from 'redux/DashboardSliceV2';
import { DeleteScenarios, DuplicateScenarios } from 'redux/BuilderModeSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';

import DeleteModal from './components/DeleteModal';
import ScenarioForm from './ScenarioForm';

export interface SelectScenarioProperties {
  refetchAllInstruments?: any;
  planPermission: boolean;
}

const SelectScenario = ({ planPermission }: SelectScenarioProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const compareReference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const timeFilter = useSelector(getTimeFilter);
  const scenarios = useSelector(getAllScenarios);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const { modelId } = useParams();
  const [scenariosPopover, setScenariosPopover] = useBoolean();
  const [compareScenariosPopover, setCompareScenariosPopover] = useBoolean();
  const [scenarioComparisonModal, setScenarioComparisonModal] = useBoolean();
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [isMobile] = useMediaQuery('(max-width: 681px)');
  const [isSmallDevice] = useMediaQuery('(max-width: 1100px)');
  const isPageBarOpen = (useSelector(getIsPageBarOpen) && isSmallDevice) || isSmallDevice;
  const [addScenarioPopover, setAddScenarioPopover] = useBoolean();
  const [showCompareSection, setShowCompareSection] = useState(false);
  const [displayInfo, setDisplayInfo]: any = useState(null);
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [editScenarioData, setEditScenarioData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isPermissionComponent, setIsPermissionComponent] = useState(false);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);

  const selectCompareScenario = (scenario: any) => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[dashboard.model_id] || {};
    modalFilters.compareScenario = scenario;
    localStorage.setItem(
      'global-modal-filters',
      JSON.stringify({ ...data, [dashboard.model_id]: modalFilters }),
    );
    dispatch(selectCompareDashboardScenario(scenario));
    if (
      dashboard?.id &&
      selectedPage?.id &&
      selectedScenario?.id &&
      timeFilter?.start &&
      timeFilter?.end
    ) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage.id,
          scenarioId: selectedScenario.id,
        }),
      );
    }
  };

  const selectScenario = (scenario: any) => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[dashboard.model_id] || {};
    modalFilters.scenario = scenario;
    localStorage.setItem(
      'global-modal-filters',
      JSON.stringify({ ...data, [dashboard.model_id]: modalFilters }),
    );
    dispatch(selectDashboardScenario(scenario));
    if (compareScenario?.id === scenario?.id) {
      selectCompareScenario(null);
    }
    if (dashboard?.id) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage?.id,
          scenarioId: scenario?.id,
          start_date: timeFilter?.start || '',
          end_date: timeFilter?.end || '',
        }),
      );
    }
  };

  const deleteScenario = async (scenarioId: number) => {
    dispatch(DeleteScenarios(scenarioId));
  };

  const onDeleteModel = async () => {
    const wasSelectedScenario = selectedScenario?.id === deleteIdModal;
    const wasCompareScenario = compareScenario?.id === deleteIdModal;

    await deleteScenario(deleteIdModal);
    const fetchResult = await dispatch(fetchAllScenarios(modelId));
    const updatedScenarios = fetchResult?.payload || scenarios;

    await setIsDeleteModal(false);
    await setDeleteIdModal(null);
    await setDeleteNameModal('');

    if (wasSelectedScenario) {
      const baseScenario = updatedScenarios.find((scenario: Scenario) => scenario?.is_base);
      const scenarioToSelect = baseScenario || updatedScenarios[0];
      if (scenarioToSelect) {
        selectScenario(scenarioToSelect);
      }
    }

    if (wasCompareScenario) {
      const remainingScenarios = updatedScenarios.filter(
        (scenario: Scenario) => scenario?.id !== selectedScenario?.id,
      );
      if (remainingScenarios.length > 0) {
        const nextCompare =
          remainingScenarios.find((scenario: Scenario) => scenario?.is_base) ||
          remainingScenarios[0];
        if (nextCompare) {
          selectCompareScenario(nextCompare);
        } else {
          selectCompareScenario(null);
        }
      } else {
        selectCompareScenario(null);
      }
    }
  };

  useEffect(() => {
    if (scenarios?.length === 1 && compareScenario) {
      selectCompareScenario(null);
    }
  }, [scenarios?.length, compareScenario]);

  // Keep the "Compare" toggle in sync with an already-set compare scenario
  // (e.g. restored from localStorage) so the field shows on open.
  useEffect(() => {
    if (compareScenario) {
      setShowCompareSection(true);
    }
  }, [compareScenario]);

  useOutsideClick({
    ref: reference,
    handler: () => {
      setScenariosPopover.off();
      setDisplayInfo(null);
      setRenameItemId(0);
      setAddScenarioPopover.off();
      setEditScenarioData(null);
      setIsEdit(false);
    },
  });

  useOutsideClick({
    ref: compareReference,
    handler: () => {
      setCompareScenariosPopover.off();
    },
  });

  const toggleInfoPopup = (event: any, id: number | null) => {
    event.stopPropagation();
    if (displayInfo === id) {
      return setDisplayInfo(null);
    }
    return setDisplayInfo(id);
  };

  const renameItem = (event: any, scenario: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setDisplayInfo(null);
    if (renameItemId === scenario?.id) {
      setRenameItemId(0);
      setEditScenarioData(null);
      setIsEdit(false);
    }
    setRenameItemId(scenario?.id);
    setAddScenarioPopover.on();
    setEditScenarioData(scenario);
    setIsEdit(true);
  };

  const copyScenario = async (event: any, item: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setDisplayInfo(null);
    const requestBody = {
      scenarioId: item?.id,
      data: {
        name: `${item?.name} - Copy`,
      },
      toaster: {
        successMessage: 'Successfully duplicated scenario',
        errorMessage: 'Failed to duplicate',
      },
    };
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      await dispatch(DuplicateScenarios(requestBody));
      await dispatch(fetchAllScenarios(modelId));
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setIsPermissionComponent(true);
      setScenariosPopover.on();
    } else {
      setIsPermissionComponent(false);
      await dispatch(DuplicateScenarios(requestBody));
      await dispatch(fetchAllScenarios(modelId));
    }
  };

  const deleteScenarioModal = async (event: any, id: number, name: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteNameModal(name);
  };

  const handleCancel = () => {
    setAddScenarioPopover.off();
    setEditScenarioData(null);
    setIsEdit(false);
  };

  const OpenPopover = () => {
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setAddScenarioPopover.on();
      setIsPermissionComponent(true);
    } else if (planPermission === true) {
      setAddScenarioPopover.off();
      setIsPermissionComponent(false);
    } else {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    }
    setEditScenarioData(null);
    setIsEdit(false);
  };

  useEffect(() => {
    setIsPermissionComponent(false);
  }, [UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model]);

  const scenariosLength = scenarios?.length ?? 0;

  return (
    <Flex alignItems='center' gap={0} maxWidth={'400px'} width={'auto'}>
      {scenarios?.length > 0 && (
        <Flex
          alignItems='center'
          gap={0}
          backgroundColor={'white'}
          padding={'9px 11px'}
          borderRadius={'9px 0 0 9px'}
          height={'32px'}
          cursor={planPermission ? 'not-allowed' : 'pointer'}
          onClick={planPermission ? undefined : setScenarioComparisonModal.on}
          opacity={planPermission ? 0.6 : 1}
        >
          <Flex
            textAlign='left'
            alignSelf='left'
            height={'100%'}
            transition='all 0.3s ease-out'
            justifyContent='flex-start'
            alignItems='center'
            gap={2}
            backgroundColor={'transparent'}
            width='fit-content'
            title={`${selectedScenario?.name || 'No scenario selected'} ${
              compareScenario ? `vs ${compareScenario?.name || ''}` : ''
            }`}
          >
            {isPageBarOpen ? (
              <BranchIcon height={7} width={7} />
            ) : (
              <>
                <Flex width='auto' alignItems='center' title={selectedScenario?.name}>
                  <Box width='8px' height='8px' borderRadius='50%' bg={'#3BA7FA'} mr={2} />
                  <Text
                    alignContent='center'
                    color='#121826'
                    fontFamily='Roboto Regular'
                    fontSize='13px'
                    whiteSpace='nowrap'
                    overflow='hidden'
                    textOverflow={'ellipsis'}
                  >
                    {selectedScenario?.name || 'Select Scenario'}
                  </Text>
                </Flex>
                {compareScenario && (
                  <>
                    <Text color='#121826' fontSize='xs' fontWeight='600' px={1}>
                      vs
                    </Text>
                    <Flex width='auto' alignItems='center' title={compareScenario?.name}>
                      <Box width='8px' height='8px' borderRadius='50%' bg='#6562CF' mr={2} />
                      <Text
                        alignContent='center'
                        color='#121826'
                        fontSize='xs'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow={'ellipsis'}
                      >
                        {compareScenario?.name}
                      </Text>
                    </Flex>
                  </>
                )}
              </>
            )}
            <FigmaTriangleDown width={9} height={6} color='#8B5CF6' />
          </Flex>
        </Flex>
      )}

      {/* Scenario Comparison — centered modal with backdrop, scaled to 0.90x */}
      <Modal isOpen={scenarioComparisonModal} onClose={setScenarioComparisonModal.off} isCentered>
        <ModalOverlay />
        <ModalContent
          boxShadow='0px 4px 12px rgba(0, 0, 0, 0.25)'
          bg='white'
          width='338px'
          maxWidth='90vw'
          borderRadius='14px'
          p='18px'
        >
          <Box textAlign='center'>
            <Text color='#121826' fontFamily='Comfortaa' fontWeight='bold' fontSize='18px'>
              Scenario Comparison
            </Text>
            <Text color='#708090' fontFamily='Roboto Regular' fontSize='13px' mt={2}>
              Choose which scenarios to compare in variance cards and KPIs
            </Text>
          </Box>
          <ModalBody px={0} pb={0} pt='18px'>
            <Flex direction='column' gap='18px'>
              {/* Base Scenario Dropdown */}
              <FormControl>
                <FormLabel
                  color='#708090'
                  fontFamily='Roboto Regular'
                  fontWeight='600'
                  fontSize='11px'
                  mb='4px'
                >
                  Base Scenario
                </FormLabel>
                <Popover
                  placement='bottom-start'
                  closeOnBlur={false}
                  isOpen={scenariosPopover}
                  onOpen={planPermission ? undefined : setScenariosPopover.on}
                  onClose={setScenariosPopover.off}
                >
                  <Box ref={reference}>
                    <PopoverTrigger>
                      <Flex
                        alignItems='center'
                        justifyContent='space-between'
                        bg='white'
                        border='1px solid #D9D9D9'
                        borderRadius='9px'
                        p='11px'
                        cursor={planPermission ? 'not-allowed' : 'pointer'}
                        _hover={planPermission ? {} : { borderColor: '#8B5CF6' }}
                        opacity={planPermission ? 0.6 : 1}
                      >
                        <Flex alignItems='center' gap='11px' flex={1}>
                          <Box width='7px' height='7px' borderRadius='50%' bg='#3BA7FA' />
                          <Text fontFamily='Roboto Regular' fontSize='13px' color='#121826'>
                            {selectedScenario?.name || 'Select Scenario'}
                          </Text>
                        </Flex>
                        <FigmaTriangleDown width={9} height={5} color='#8B8AAB' />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent
                      boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
                      bg='#E8E9EB'
                      borderRadius='14px'
                      border='none'
                      overflow='hidden'
                      minW='302px'
                      maxH='60vh'
                    >
                      <PopoverBody p={0} maxH='60vh' overflowY='auto'>
                        <Flex direction='column' gap='1px'>
                          {scenarios?.length > 0 &&
                            scenarios.map((scenario: Scenario, index: number) => (
                              <Flex
                                key={scenario.id}
                                alignItems='center'
                                gap='9px'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                bg='white'
                                px='14px'
                                py='11px'
                                onClick={
                                  planPermission
                                    ? undefined
                                    : () => {
                                        selectScenario(scenario);
                                        setScenariosPopover.off();
                                      }
                                }
                                onDoubleClick={(event) => {
                                  event.stopPropagation();
                                  setRenameItemId(scenario?.id);
                                }}
                              >
                                <Text
                                  flex={1}
                                  fontFamily='Roboto Regular'
                                  fontSize='13px'
                                  color='#121826'
                                  whiteSpace='nowrap'
                                  overflow='hidden'
                                  textOverflow='ellipsis'
                                  onClick={() => setDisplayInfo(null)}
                                >
                                  {scenario?.name}
                                </Text>
                                {selectedScenario?.id === scenario?.id ? (
                                  <FigmaCheckCircleIcon width={14} height={14} color='#8B5CF6' />
                                ) : (
                                  <Box
                                    width='14px'
                                    height='14px'
                                    borderRadius='50%'
                                    bg='white'
                                    border='1px solid #C4B5FD'
                                  />
                                )}
                                <Flex alignItems='center' position='relative'>
                                  <Flex
                                    alignItems='center'
                                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                                    onClick={
                                      planPermission
                                        ? undefined
                                        : (event) => toggleInfoPopup(event, scenario.id)
                                    }
                                  >
                                    <FigmaMenuDotsIcon width={14} height={14} color='#121826' />
                                  </Flex>
                                  <Flex
                                    bgColor='white'
                                    width='fit-content'
                                    position='absolute'
                                    display={displayInfo === scenario.id ? 'block' : 'none'}
                                    zIndex='dropdown'
                                    top={
                                      scenariosLength > 3 && index >= scenariosLength - 3
                                        ? '-100px'
                                        : '28px'
                                    }
                                    right='0'
                                    p='5px 0'
                                    filter='drop-shadow(0px 0px 15.998px rgba(0, 0, 0, 0.25))'
                                    borderRadius='4px'
                                  >
                                    <Box
                                      p='5px 15px'
                                      _hover={{
                                        background: '#EEF2F6',
                                        color: 'black',
                                      }}
                                    >
                                      <Text
                                        color='black'
                                        fontSize={'xs'}
                                        onClick={
                                          planPermission
                                            ? undefined
                                            : (event) => renameItem(event, scenario)
                                        }
                                      >
                                        Edit
                                      </Text>
                                    </Box>
                                    <Box
                                      p='5px 15px'
                                      _hover={{
                                        background: '#EEF2F6',
                                        color: 'black',
                                      }}
                                    >
                                      <Text
                                        color='black'
                                        fontSize={'xs'}
                                        onClick={
                                          planPermission
                                            ? undefined
                                            : (event) => copyScenario(event, scenario)
                                        }
                                      >
                                        Duplicate
                                      </Text>
                                    </Box>
                                    {Number(scenarios?.length) !== 1 && (
                                      <Box
                                        p='5px 15px'
                                        _hover={{
                                          background: '#EEF2F6',
                                          color: 'black',
                                        }}
                                      >
                                        <Text
                                          color='black'
                                          fontSize={'xs'}
                                          onClick={
                                            planPermission
                                              ? undefined
                                              : (event) => {
                                                  deleteScenarioModal(
                                                    event,
                                                    scenario?.id,
                                                    scenario?.name,
                                                  );
                                                }
                                          }
                                        >
                                          Delete
                                        </Text>
                                      </Box>
                                    )}
                                  </Flex>
                                </Flex>
                              </Flex>
                            ))}
                        </Flex>
                        <Popover
                          placement={isMobile ? 'bottom' : 'right-start'}
                          closeOnBlur={false}
                          isOpen={addScenarioPopover}
                          onOpen={planPermission ? undefined : OpenPopover}
                          onClose={setAddScenarioPopover.off}
                          isLazy
                          lazyBehavior='keepMounted'
                        >
                          <PopoverTrigger>
                            <Flex
                              align='center'
                              gap='7px'
                              bg='white'
                              px='14px'
                              py='11px'
                              cursor={planPermission ? 'not-allowed' : 'pointer'}
                            >
                              <FigmaPlusIcon width={8} height={8} color='#8B5CF6' />
                              <Text
                                fontFamily='Roboto Medium, Roboto Regular'
                                fontWeight='500'
                                fontSize='13px'
                                color='#8B5CF6'
                              >
                                Add New Scenario
                              </Text>
                            </Flex>
                          </PopoverTrigger>
                          <PopoverContent
                            p={5}
                            filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                            bg='white'
                            borderRadius='14px'
                          >
                            <ScenarioForm
                              onCancel={handleCancel}
                              editScenarioData={editScenarioData}
                              isEdit={isEdit}
                              setIsEdit={setIsEdit}
                            />
                          </PopoverContent>
                        </Popover>
                        {isPermissionComponent && (
                          <ScenarioPermission
                            ScenarioLimit={
                              UserSubscriptionLimitData?.subscription_features_limit
                                ?.scenario_per_model
                            }
                            CurrentScenarioLength={scenarios?.length}
                          />
                        )}
                      </PopoverBody>
                    </PopoverContent>
                  </Box>
                </Popover>
              </FormControl>

              {/* Compare toggle — controls whether the Compare Against field is
                  shown at all. Turning off also clears any active compare
                  scenario via the existing selectCompareScenario(null) action. */}
              {scenarios?.length > 1 && (
                <Flex align='center' gap='11px' bg='#F5F0FF' borderRadius='11px' px='11px' py='9px'>
                  <FigmaCodeCompareIcon width={14} height={14} color='#121826' />
                  <Text flex={1} fontFamily='Roboto Regular' fontSize='13px' color='#121826'>
                    Compare
                  </Text>
                  <Switch
                    size='sm'
                    isChecked={showCompareSection}
                    colorScheme='purple'
                    isDisabled={planPermission}
                    onChange={() => {
                      const next = !showCompareSection;
                      setShowCompareSection(next);
                      if (!next && compareScenario) {
                        selectCompareScenario(null);
                      }
                    }}
                  />
                </Flex>
              )}

              {/* Compare Against Dropdown */}
              {scenarios?.length > 1 && showCompareSection && (
                <FormControl>
                  <FormLabel
                    color='#708090'
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    mb='4px'
                  >
                    Compare Against
                  </FormLabel>
                  <Popover
                    placement='bottom-start'
                    closeOnBlur={false}
                    isOpen={compareScenariosPopover}
                    onOpen={planPermission ? undefined : setCompareScenariosPopover.on}
                    onClose={setCompareScenariosPopover.off}
                  >
                    <Box ref={compareReference}>
                      <PopoverTrigger>
                        <Flex
                          alignItems='center'
                          justifyContent='space-between'
                          bg='white'
                          border='1px solid #D9D9D9'
                          borderRadius='9px'
                          p='11px'
                          cursor={planPermission ? 'not-allowed' : 'pointer'}
                          _hover={planPermission ? {} : { borderColor: '#8B5CF6' }}
                          opacity={planPermission ? 0.6 : 1}
                        >
                          <Flex alignItems='center' gap='11px' flex={1}>
                            <Box width='7px' height='7px' borderRadius='50%' bg='#22C55E' />
                            <Text fontFamily='Roboto Regular' fontSize='13px' color='#121826'>
                              {compareScenario?.name || 'Select Scenario'}
                            </Text>
                          </Flex>
                          <FigmaTriangleDown width={9} height={5} color='#8B8AAB' />
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent
                        boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
                        bg='#E8E9EB'
                        borderRadius='14px'
                        border='none'
                        overflow='hidden'
                        minW='302px'
                        maxH='60vh'
                      >
                        <PopoverBody p={0} maxH='60vh' overflowY='auto'>
                          <Flex direction='column' gap='1px'>
                            {scenarios?.length > 0 &&
                              scenarios.map((scenario: Scenario) => {
                                if (selectedScenario?.id === scenario?.id) {
                                  return null;
                                }
                                const isCompareSelected = compareScenario?.id === scenario?.id;
                                return (
                                  <Flex
                                    key={scenario.id}
                                    alignItems='center'
                                    gap='9px'
                                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                                    bg='white'
                                    px='14px'
                                    py='11px'
                                    onClick={
                                      planPermission
                                        ? undefined
                                        : () => {
                                            if (isCompareSelected) {
                                              selectCompareScenario(null);
                                            } else {
                                              selectCompareScenario(scenario);
                                            }
                                            setCompareScenariosPopover.off();
                                          }
                                    }
                                  >
                                    <Text
                                      flex={1}
                                      fontFamily='Roboto Regular'
                                      fontSize='13px'
                                      color='#121826'
                                      whiteSpace='nowrap'
                                      overflow='hidden'
                                      textOverflow='ellipsis'
                                    >
                                      {scenario?.name}
                                    </Text>
                                    {isCompareSelected ? (
                                      <FigmaCheckCircleIcon
                                        width={14}
                                        height={14}
                                        color='#8B5CF6'
                                      />
                                    ) : (
                                      <Box
                                        width='14px'
                                        height='14px'
                                        borderRadius='50%'
                                        bg='white'
                                        border='1px solid #C4B5FD'
                                      />
                                    )}
                                    {/* Matches the width of the 3-dot menu column in the
                                        Base Scenario list so both dropdowns' checkmarks
                                        line up at the same position. */}
                                    <Box width='23px' flexShrink={0} />
                                  </Flex>
                                );
                              })}
                            {compareScenario && (
                              <Flex
                                alignItems='center'
                                justifyContent='center'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                bg='white'
                                px='14px'
                                py='11px'
                                onClick={
                                  planPermission
                                    ? undefined
                                    : () => {
                                        selectCompareScenario(null);
                                        setCompareScenariosPopover.off();
                                      }
                                }
                              >
                                <Text
                                  fontFamily='Roboto Medium, Roboto Regular'
                                  fontWeight='500'
                                  fontSize='13px'
                                  color='#8B5CF6'
                                >
                                  Clear Compare
                                </Text>
                              </Flex>
                            )}
                          </Flex>
                        </PopoverBody>
                      </PopoverContent>
                    </Box>
                  </Popover>
                </FormControl>
              )}

              {/* Both buttons only close the modal — every selection above
                  already applies instantly via the existing dispatch calls. */}
              <Flex gap='18px'>
                <Button
                  flex={1}
                  bg='white'
                  border='1px solid #D9D9D9'
                  borderRadius='11px'
                  fontFamily='Roboto Regular'
                  fontWeight='500'
                  fontSize='13px'
                  color='#121826'
                  p='14px'
                  height='auto'
                  _hover={{ bg: 'gray.50' }}
                  onClick={setScenarioComparisonModal.off}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  bg='#8B5CF6'
                  borderRadius='11px'
                  fontFamily='Roboto Bold'
                  fontWeight='700'
                  fontSize='13px'
                  color='white'
                  p='14px'
                  height='auto'
                  _hover={{ bg: '#7C4FE0' }}
                  onClick={setScenarioComparisonModal.off}
                >
                  Apply
                </Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
    </Flex>
  );
};

export default PlanPermission(SelectScenario);
